import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaPaperPlane, FaCheck } from 'react-icons/fa';
import { db } from '../FirebaseComp/firebase'; // Adjust the path to your firebase config
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './contactus.css';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // Save to Firestore
        const docRef = await addDoc(collection(db, 'contacts'), {
          ...formData,
          timestamp: serverTimestamp()
        });
        
        console.log('Document written with ID: ', docRef.id);
        setSubmitSuccess(true);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: ''
          });
          setSubmitSuccess(false);
        }, 3000);
      } catch (error) {
        console.error('Error adding document: ', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };



  const contactMethods = [
    {
      icon: <FaMapMarkerAlt className="contact-icon" />,
      title: "Our Location",
      info: "932, Maradana Rd, Colombo 08, Sri Lanka",
      link: "https://g.co/kgs/tFWGqrR"
    },
    {
      icon: <FaPhone className="contact-icon" />,
      title: "Phone Numbers",
      info: "+94 112 682 277 \n+94 773 440 937 ",
      link: null
    },
    {
      icon: <FaEnvelope className="contact-icon" />,
      title: "Email Address",
      info: "info@optometryassociation.lk",
      link: null
    },
    {
      icon: <FaClock className="contact-icon" />,
      title: "Working Hours",
      info: "Mon - Fri: 8:30 AM - 5:00 PM\nSat: 9:00 AM - 1:00 PM",
      link: null
    }
  ];

  return (
    <div className="contact-us-container">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-overlay"></div>
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className='cecoa-heading'>Contact Us</h1>
          <p className="cecoa-subtitle">We're here to help with all your association needs</p>
        </motion.div>
      </section>

      {/* Contact Information */}
      <section className="contact-info-section">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>Get In Touch</h2>
          <div className="divider"></div>
          <p className="section-description">
            Reach out to us through any of these channels or fill out the contact form below.
          </p>
        </motion.div>

        <div className="contact-methods-grid">
          {contactMethods.map((method, index) => (
            <motion.div 
              key={index}
              className="contact-method-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="contact-icon-container">
                {method.icon}
              </div>
              <h3>{method.title}</h3>
              <p className="contact-info">
                {method.info.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
              {method.link && (
                <a 
                  href={method.link} 
                  className="contact-link"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  View on map
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="form-container">
          <motion.div 
            className="form-header"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Send Us a Message</h2>
            <div className="divider"></div>
            <p>
              Have questions about our services or need to schedule an appointment? 
              Fill out the form below and we'll get back to you promptly.
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="contact-form">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="form-group">
                <label htmlFor="name">Full Name*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
            </motion.div>

            <div className="form-row">
              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <label htmlFor="email">Email Address*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="form-group">
                <label htmlFor="subject">Subject*</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={errors.subject ? 'error' : ''}
                />
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="form-group">
                <label htmlFor="message">Your Message*</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? 'error' : ''}
                ></textarea>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting || submitSuccess}
              >
                {isSubmitting ? (
                  'Sending...'
                ) : submitSuccess ? (
                  <>
                    <FaCheck /> Sent Successfully!
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> Send Message
                  </>
                )}
              </button>
            </motion.div>
          </form>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>Our Location</h2>
          <div className="divider"></div>
        </motion.div>
        
        <motion.div 
          className="map-container"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3259.3682968893004!2d79.86938077399755!3d6.920300993079338!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae259744a1d7131%3A0x614fbd9988cf7417!2sEye%20Care%20Institute!5e1!3m2!1sen!2slk!4v1746178565593!5m2!1sen!2slk" 
            width="100%" 
            height="450" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy"
            title="Ceylon Eye Care Location"
          ></iframe>
        </motion.div>
      </section>
    </div>
  );
}

export default ContactUs;